import type { MatchupDraft, MatchupReport, MatchupReportIndex } from "@averagejoesff/shared";
import { publishMatchupDraft } from "../../api/adminApi";
import { AdminFormStatus } from "../../components/AdminFormStatus";
import { useAdminForm } from "../../hooks/useAdminForm";
import { formatGeneratedAt } from "../../lib/format";

interface MatchupDraftPanelProps {
  draft: MatchupDraft;
  idToken: string;
  previewing: boolean;
  onTogglePreview: () => void;
  /** Called with the server's response so the caller can update state directly. */
  onPublished: (result: { report: MatchupReport; index: MatchupReportIndex }) => void;
}

/** Review step for the weekly fetcher's generated report: preview it in place, then publish. */
export function MatchupDraftPanel({ draft, idToken, previewing, onTogglePreview, onPublished }: MatchupDraftPanelProps) {
  const { status, error, submit } = useAdminForm(() => publishMatchupDraft(idToken), onPublished);
  const { season, week } = draft.report;

  return (
    <section className="admin-panel matchup-draft" aria-label="Matchup report draft">
      <p className="matchup-draft__title">
        Draft ready: {season} · Week {week}
      </p>
      <p className="admin-panel__hint">
        Generated {formatGeneratedAt(draft.createdAt)} ·{" "}
        {draft.writtenBy === "claude" ? "written by Claude from computed stats" : "computed wording only"}. Only
        signed-in admins can see it until it's published.
      </p>
      {draft.usage && (
        <p className="admin-panel__hint">
          Claude call: {draft.usage.inputTokens.toLocaleString()} input /{" "}
          {draft.usage.outputTokens.toLocaleString()} output tokens ({draft.usage.model})
        </p>
      )}
      {draft.notes.length > 0 && (
        <ul className="matchup-draft__notes">
          {draft.notes.map((note, i) => (
            <li key={i}>{note}</li>
          ))}
        </ul>
      )}
      <div className="admin-panel__actions">
        <button type="button" onClick={onTogglePreview}>
          {previewing ? "Hide preview" : "Preview draft"}
        </button>
        <button type="button" className="matchup-draft__publish" onClick={() => void submit()} disabled={status === "pending"}>
          {status === "pending" ? "Publishing…" : "Publish"}
        </button>
        <AdminFormStatus status={status} error={error} />
      </div>
    </section>
  );
}
